'use client';

import React, { useState, useEffect, Suspense, useMemo, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { trackEvent } from '@/components/analytics';
import { useQuizState } from './hooks/useQuizState';
import { useQuizTariffs } from './hooks/useQuizTariffs';
import { generateQuestions, generateTranslations } from './utils/generateQuestions';
import { filterTariffs } from './utils/filterTariffs';
import { scenarioToAnswers } from './utils/scenarioToAnswers';
import QuizQuestion from './QuizQuestion';
import QuizResults from './QuizResults';
import ESimErrorBoundary from '../ESimErrorBoundary';
import { CurrencyProvider } from '@/components/common/CurrencyContext';
import { t as tr } from '@/i18n/translations';

// eSIM category color
const ESIM_COLOR = '#9333ea'; // purple-600

// Loading component
function ESimQuizLoading({ lang = 'ru' }) {
    const ui = tr(lang, 'esimToolsUi').quiz;
    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50 flex items-center justify-center">
            <div className="flex flex-col items-center">
                <div 
                    className="animate-spin rounded-full h-16 w-16 border-2 border-t-transparent"
                    style={{ borderColor: `${ESIM_COLOR}30`, borderTopColor: ESIM_COLOR }}
                />
                <p className="mt-6 article-serif text-gray-500">{ui.loading}</p>
            </div>
        </div>
    );
}

// Search params wrapper
function SearchParamsWrapper({ children }) {
    const searchParams = useSearchParams();
    const shouldShowResults = searchParams.get('showResults') === 'true';
    const scenario = searchParams.get('scenario');
    return children({ shouldShowResults, scenario });
}

// Main quiz content component
function ESimQuizContent({
    lang = 'ru',
    shouldShowResults = false,
    countryData = null,
    countrySlug = 'montenegro',
    scenario = null,
}) {
    const [isClient, setIsClient] = useState(false);
    const [showResults, setShowResults] = useState(shouldShowResults);
    const [filteredTariffs, setFilteredTariffs] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'price', direction: 'asc' });
    const [showAllTariffs, setShowAllTariffs] = useState(false);
    const [scenarioApplied, setScenarioApplied] = useState(false);

    const ui = tr(lang, 'esimToolsUi').quiz;

    // Use custom hooks
    const {
        currentQuestion,
        setCurrentQuestion,
        answers,
        setAnswers,
        error,
        setError,
        clearQuizState
    } = useQuizState(lang, isClient);

    const {
        allTariffs,
        loading,
        error: tariffsError
    } = useQuizTariffs(countrySlug, lang, isClient);

    // Generate questions and translations
    const questions = useMemo(() => generateQuestions(countryData, lang), [countryData, lang]);
    const translations = useMemo(() => generateTranslations(countryData, lang), [countryData, lang]);
    const t = translations[lang];

    // Client-side initialization
    useEffect(() => {
        setIsClient(true);
    }, []);

    // Apply scenario preset answers if scenario is provided
    useEffect(() => {
        if (isClient && scenario && !scenarioApplied) {
            const timer = setTimeout(() => {
                clearQuizState();
                const presetAnswers = scenarioToAnswers(scenario);
                if (Object.keys(presetAnswers).length > 0) {
                    setAnswers(presetAnswers);
                    setScenarioApplied(true);
                    setShowResults(true);
                }
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [isClient, scenario, scenarioApplied, setAnswers, clearQuizState]);

    // Filter tariffs when answers change or tariffs are loaded
    useEffect(() => {
        if (allTariffs.length > 0 && Object.keys(answers).length > 0) {
            const filtered = filterTariffs(allTariffs, answers, countryData, lang);
            setFilteredTariffs(filtered);
        } else if (allTariffs.length > 0 && Object.keys(answers).length === 0) {
            setFilteredTariffs([]);
        }
    }, [allTariffs, answers, countryData, lang]);

    // Calculate total questions (excluding ru-only questions for English)
    const totalQuestions = useMemo(() => {
        return lang === 'en' ? questions.filter(q => !q.ruOnly).length : questions.length;
    }, [questions, lang]);

    // Handle answer selection
    const handleAnswerChange = useCallback((questionIndex, newAnswers) => {
        setAnswers(prev => ({
            ...prev,
            [questionIndex]: newAnswers
        }));
        setError('');
    }, [setAnswers, setError]);

    // Handle next question
    const handleNext = useCallback(() => {
        const currentAnswers = answers[currentQuestion] || [];
        if (currentAnswers.length === 0) {
            setError(t.selectAtLeastOne);
            return;
        }

        let nextQuestion = currentQuestion + 1;
        while (nextQuestion < questions.length) {
            const question = questions[nextQuestion];
            if (question.ruOnly && lang === 'en') {
                nextQuestion++;
                continue;
            }
            break;
        }

        if (nextQuestion < questions.length) {
            setCurrentQuestion(nextQuestion);
            setError('');
        } else {
            setShowResults(true);
            trackEvent('complete', 'esim_quiz', 'quiz_completed', {
                country: countrySlug,
                language: lang,
                answers_count: Object.keys(answers).length
            });
        }
    }, [currentQuestion, answers, questions, lang, setCurrentQuestion, setError, t.selectAtLeastOne, countrySlug]);

    // Handle back navigation
    const handleBack = useCallback(() => {
        if (currentQuestion > 0) {
            let prevQuestion = currentQuestion - 1;
            while (prevQuestion >= 0) {
                const question = questions[prevQuestion];
                if (question.ruOnly && lang === 'en') {
                    prevQuestion--;
                    continue;
                }
                break;
            }

            if (prevQuestion >= 0) {
                setCurrentQuestion(prevQuestion);
                setError('');
            }
        }
    }, [currentQuestion, questions, lang, setCurrentQuestion, setError]);

    // Handle start over
    const handleStartOver = useCallback(() => {
        setCurrentQuestion(0);
        setAnswers({});
        setError('');
        setShowResults(false);
        setFilteredTariffs([]);
        setShowAllTariffs(false);
        setSortConfig({ key: 'price', direction: 'asc' });
        clearQuizState();
    }, [setCurrentQuestion, setAnswers, setError, clearQuizState]);

    // Handle sort change
    const handleSort = useCallback((key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    }, [sortConfig]);

    // Handle show all toggle
    const handleShowAllToggle = useCallback(() => {
        setShowAllTariffs(prev => {
            const newValue = !prev;
            if (newValue) {
                setSortConfig({ key: 'relevance', direction: 'desc' });
            } else {
                setSortConfig({ key: 'price', direction: 'asc' });
            }
            return newValue;
        });
    }, []);

    // Get tariffs with relevance scores
    const getAllTariffsWithRelevance = useCallback(() => {
        return allTariffs.map(tariff => {
            let relevanceScore = 0;

            const regionAnswer = answers[0] && answers[0][0];
            if (regionAnswer !== undefined) {
                const countryName = countryData?.name || countrySlug || 'country';
                if (regionAnswer === 0) {
                    if (tariff.regions && tariff.regions.length === 1 && tariff.regions[0] === countryName) {
                        relevanceScore += 10;
                    } else {
                        relevanceScore -= 5;
                    }
                } else if (regionAnswer === 1) {
                    const isEuropeWide = tariff.regions && tariff.regions.includes('Europe');
                    const multiCountry = tariff.regions && tariff.regions.length > 1;
                    if (isEuropeWide || multiCountry) {
                        relevanceScore += 10;
                    } else {
                        relevanceScore -= 5;
                    }
                }
            }

            const durationAnswer = answers[1] && answers[1][0];
            if (durationAnswer !== undefined && tariff.duration) {
                const validityDays = parseInt(tariff.duration);
                if (durationAnswer === 0 && validityDays <= 7) relevanceScore += 8;
                else if (durationAnswer === 1 && validityDays >= 8 && validityDays <= 15) relevanceScore += 8;
                else if (durationAnswer === 2 && validityDays >= 16 && validityDays <= 30) relevanceScore += 8;
                else if (durationAnswer === 3 && validityDays > 30) relevanceScore += 8;
                else relevanceScore -= 3;
            }

            const dataAnswer = answers[2] && answers[2][0];
            if (dataAnswer !== undefined) {
                if (dataAnswer === 0) {
                    if (tariff.data && !tariff.unlimited && parseFloat(tariff.data) <= 5) {
                        relevanceScore += 8;
                    } else {
                        relevanceScore -= 3;
                    }
                } else if (dataAnswer === 1) {
                    if (tariff.data && !tariff.unlimited) {
                        const dataAmount = parseFloat(tariff.data);
                        if (dataAmount > 5 && dataAmount < 20) {
                            relevanceScore += 8;
                        } else {
                            relevanceScore -= 3;
                        }
                    }
                } else if (dataAnswer === 2) {
                    if (tariff.unlimited || (tariff.data && parseFloat(tariff.data) >= 20)) {
                        relevanceScore += 8;
                    } else {
                        relevanceScore -= 3;
                    }
                } else if (dataAnswer === 3) {
                    if (tariff.unlimited) {
                        relevanceScore += 10;
                    } else {
                        relevanceScore -= 5;
                    }
                }
            }

            if (lang === 'ru') {
                const languageAnswer = answers[3] && answers[3][0];
                if (languageAnswer !== undefined) {
                    if (languageAnswer === 0) {
                        if (tariff.languages && tariff.languages.includes('ru')) {
                            relevanceScore += 5;
                        } else {
                            relevanceScore -= 3;
                        }
                    } else if (languageAnswer === 1) {
                        if (tariff.languages && tariff.languages.includes('en')) {
                            relevanceScore += 3;
                        }
                    }
                }
            }

            return {
                ...tariff,
                relevanceScore
            };
        }).sort((a, b) => b.relevanceScore - a.relevanceScore);
    }, [allTariffs, answers, countryData, lang, countrySlug]);

    const backHref = `/${lang}/${countrySlug}/esim`;
    const progressPct = totalQuestions > 0 ? Math.round(((currentQuestion + 1) / totalQuestions) * 100) : 0;

    // Show loading while not on client
    if (!isClient) {
        return (
            <main className="gf-root">
                <div className="qz-wrap">
                    <p className="qz-kick">{ui.kickShort}</p>
                    <h1>{t.title}</h1>
                    <p className="lead">{t.loading}</p>
                </div>
            </main>
        );
    }

    return (
        <main className="gf-root">
            <div className="qz-wrap">
                <Link className="qz-back" href={backHref}>{ui.back}</Link>
                <p className="qz-kick">{ui.kick}</p>
                <h1>{t.title}</h1>
                <p className="lead">{t.subtitle}</p>

                {loading ? (
                    <p className="lead" style={{ marginTop: 40 }}>{t.loading}</p>
                ) : tariffsError ? (
                    <div className="qz-card" style={{ marginTop: 30 }}>
                        <div className="qz-qn">{ui.errorLabel}</div>
                        <div className="qz-q">{ui.errorLoadPlans}</div>
                        <p className="qz-hint">{tariffsError}</p>
                        <div className="qz-nav">
                            <span />
                            <button type="button" className="qz-next" onClick={() => window.location.reload()}>
                                {ui.refresh}
                            </button>
                        </div>
                    </div>
                ) : showResults ? (
                    <QuizResults
                        filteredTariffs={filteredTariffs}
                        allTariffs={getAllTariffsWithRelevance()}
                        showAllTariffs={showAllTariffs}
                        onShowAllToggle={handleShowAllToggle}
                        onBackToQuestions={() => setShowResults(false)}
                        onStartOver={handleStartOver}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        translations={t}
                        lang={lang}
                        countrySlug={countrySlug}
                    />
                ) : (
                    <>
                        <div className="qz-prog">
                            <span>{ui.questionWord} {currentQuestion + 1} / {totalQuestions}</span>
                            <div className="qz-bar"><i style={{ width: `${progressPct}%` }} /></div>
                        </div>

                        <div className="qz-card">
                            <div className="qz-qn">{ui.questionPrefix + String(currentQuestion + 1).padStart(2, '0')}</div>
                            <QuizQuestion
                                question={questions[currentQuestion]}
                                lang={lang}
                                selectedAnswers={answers[currentQuestion] || []}
                                onAnswerChange={(newAnswers) => handleAnswerChange(currentQuestion, newAnswers)}
                                translations={t}
                            />
                            {error && <p className="qz-err">{error}</p>}
                            <div className="qz-nav">
                                <button type="button" className="qz-prev" onClick={handleBack} style={{ visibility: currentQuestion === 0 ? 'hidden' : 'visible' }}>
                                    ← {t.back}
                                </button>
                                <button type="button" className="qz-next" onClick={handleNext} disabled={(answers[currentQuestion] || []).length === 0}>
                                    {currentQuestion < questions.length - 1 ? t.next : t.finish} →
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </main>
    );
}

// Main export component with error boundary
export default function ESimQuizPage({ lang = 'ru', countryData = null, countrySlug = 'montenegro' }) {
    const baseCurrency = countryData?.currency === 'EUR' ? 'EUR' : 'USD';
    return (
        <ESimErrorBoundary lang={lang} countrySlug={countrySlug}>
            <Suspense fallback={<ESimQuizLoading lang={lang} />}>
                <CurrencyProvider lang={lang} baseCurrency={baseCurrency}>
                    <SearchParamsWrapper>
                        {({ shouldShowResults, scenario }) => (
                            <ESimQuizContent
                                lang={lang}
                                shouldShowResults={shouldShowResults}
                                countryData={countryData}
                                countrySlug={countrySlug}
                                scenario={scenario}
                            />
                        )}
                    </SearchParamsWrapper>
                </CurrencyProvider>
            </Suspense>
        </ESimErrorBoundary>
    );
}
